import React from 'react';
import { Avatar, Button, Fab, IconButton, Tooltip } from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowDownward,
  ArrowUpward,
  BorderColorOutlined,
  CancelOutlined,
  Create,
  FileDownload,
  FilterAltOutlined,
  GpsFixed,
  MoreVert,
  PlusOne,
  Print,
  Receipt,
  TaskAlt,
  Visibility,
} from '@mui/icons-material';
import _ from 'lodash';
import { t } from 'i18next';
import * as Icons from '@mui/icons-material';
import { PRIMARY } from '../../constants/Colors';

export interface BakeasyButtonProps {
  label?: any;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: any;
  style?: React.CSSProperties;
  startIcon?: any;
  endIcon?: any;
  size?: any;
  type?: any;
  color?: any;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  value?: any;
  tooltipTitle?: any;
}

export function BakeasyButton(props: BakeasyButtonProps) {
  return props?.tooltipTitle ? (
    <Tooltip title={props?.tooltipTitle}>
      <span style={{ flex: 1 }}>
        <Button
          startIcon={props?.startIcon ? props?.startIcon : null}
          endIcon={props?.endIcon ? props?.endIcon : null}
          variant={props?.variant ? props?.variant : 'contained'}
          disabled={props?.disabled ? props?.disabled : false}
          fullWidth={props?.fullWidth ? props?.fullWidth : false}
          size={props?.size ? props?.size : 'medium'}
          color={props?.color}
          className={props.className || ''}
          sx={{
            margin: 0,
            borderRadius: 2,
            paddingY: 1,
            color: props?.variant === 'outlined' ? 'primary' : 'white',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          style={
            props?.style !== undefined
              ? props?.style
              : { textTransform: 'initial' }
          }
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            props.onClick(event);
          }}
        >
          {t(props.label)}
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Button
      startIcon={props?.startIcon ? props?.startIcon : null}
      endIcon={props?.endIcon ? props?.endIcon : null}
      variant={props?.variant ? props?.variant : 'contained'}
      disabled={props?.disabled ? props?.disabled : false}
      fullWidth={props?.fullWidth ? props?.fullWidth : false}
      size={props?.size ? props?.size : 'medium'}
      color={props?.color}
      className={props.className || ''}
      sx={{
        margin: 0,
        borderRadius: 2,
        paddingY: 1,
        color: props?.variant === 'outlined' ? 'primary' : 'white',
      }}
      style={
        props?.style !== undefined ? props?.style : { textTransform: 'initial' }
      }
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        props.onClick(event);
      }}
    >
      {props.label}
    </Button>
  );
}

export interface BakeasyButtonIconProps {
  color?: any;
  label?: string;
  disabled?: boolean;
  icon?: any;
  tooltipTitle?: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function stringAvatar(name: any) {
  return {
    sx: { bgcolor: PRIMARY },
    children: `${_.capitalize(name?.slice(0, 1))}`,
  };
}

export function BakeasyButtonIcon(props: BakeasyButtonIconProps) {
  return (
    <Tooltip title={props.tooltipTitle}>
      {props?.icon ? (
        <IconButton color={props.color} size="small" onClick={props.onClick}>
          {props?.icon}
        </IconButton>
      ) : (
        <IconButton color={props.color} onClick={props.onClick}>
          <Avatar {...stringAvatar(props?.label?.toString())}></Avatar>
        </IconButton>
      )}
    </Tooltip>
  );
}

export function BakeasyButtonIconView(props: BakeasyButtonIconProps) {
  return (
    <BakeasyButtonIcon {...props} color={'primary'} icon={<Visibility />} />
  );
}

export function BakeasyButtonIconApprove(props: BakeasyButtonIconProps) {
  return <BakeasyButtonIcon {...props} color={'success'} icon={<TaskAlt />} />;
}

export function BakeasyButtonIconReject(props: BakeasyButtonIconProps) {
  return (
    <BakeasyButtonIcon {...props} color={'error'} icon={<CancelOutlined />} />
  );
}

export function BakeasyButtonIconEdit(props: BakeasyButtonIconProps) {
  return (
    <BakeasyButtonIcon
      {...props}
      color={'primary'}
      icon={<BorderColorOutlined />}
    />
  );
}

export function BakeasyButtonIconReCenter(props: BakeasyButtonIconProps) {
  return (
    <Fab color="default" size="medium" aria-label="add">
      <BakeasyButtonIcon {...props} color={'primary'} icon={<GpsFixed />} />
    </Fab>
  );
}

export function BakeasyButtonIconViewMore(props: BakeasyButtonIconProps) {
  return <BakeasyButtonIcon {...props} color="default" icon={<MoreVert />} />;
}

export function BakeasyButtonIconBack(props: BakeasyButtonIconProps) {
  return <BakeasyButtonIcon {...props} color="default" icon={<ArrowBack />} />;
}

export function BakeasyButtonIconAdd(props: BakeasyButtonIconProps) {
  return <BakeasyButtonIcon {...props} color="default" icon={<PlusOne />} />;
}

export function BakeasyButtonIconArrowUp(props: BakeasyButtonIconProps) {
  return (
    <BakeasyButtonIcon {...props} color="default" icon={<ArrowUpward />} />
  );
}

export function BakeasyButtonIconArrowDown(props: BakeasyButtonIconProps) {
  return (
    <BakeasyButtonIcon {...props} color="default" icon={<ArrowDownward />} />
  );
}

export function BakeasyButtonExport(props: BakeasyButtonProps) {
  return (
    <BakeasyButton
      {...props}
      label={t('EXPORT')}
      startIcon={<FileDownload />}
    />
  );
}

export function BakeasyButtonFilter(props: BakeasyButtonProps) {
  return (
    <BakeasyButton
      {...props}
      label={t('FILTER')}
      startIcon={<FilterAltOutlined />}
    />
  );
}

export function BakeasyButtonAdd(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('ADD')} startIcon={<Add />} />;
}

export function BakeasyButtonCreate(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CREATE')} />;
}

export function BakeasyButtonCancel(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CANCEL')} variant={'outlined'} />;
}

export function BakeasyButtonCompose(props: BakeasyButtonProps) {
  return (
    <BakeasyButton {...props} label={t('COMPOSE')} startIcon={<Create />} />
  );
}

export function BakeasyButtonPrint(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('PRINT')} startIcon={<Print />} />;
}

export function BakeasyButtonInvoice(props: BakeasyButtonProps) {
  return (
    <BakeasyButton {...props} label={t('INVOICE')} startIcon={<Receipt />} />
  );
}

export function BakeasyUpdateInvoiceDetails(props: BakeasyButtonProps) {
  return (
    <BakeasyButton
      {...props}
      label={t('UPDATE_INVOICE_DETAILS')}
      startIcon={<Receipt />}
    />
  );
}

export function BakeasyButtonUpdate(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('UPDATE')} />;
}

export function BakeasyButtonClose(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} variant={'outlined'} label={t('CLOSE')} />;
}

export function BakeasyButtonAssign(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('ASSIGN')} />;
}

export function BakeasyButtonUnassign(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('UNASSIGN')} color="error" />;
}
export function BakeasyButtonClear(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CLEAR')} />;
}
export function BakeasyButtonNewMsg(props: BakeasyButtonProps) {
  return (
    <BakeasyButton
      {...props}
      label={t('NEW_MESSAGE')}
      startIcon={<Icons.Create />}
    />
  );
}
export function BakeasyButtonSend(props: BakeasyButtonProps) {
  return (
    <BakeasyButton {...props} label={t('SEND')} startIcon={<Icons.Send />} />
  );
}
export function BakeasyButtonStatus(props: BakeasyButtonProps) {
  return (
    <BakeasyButton {...props} size={props?.size ? props?.size : 'medium'} />
  );
}

export function BakeasyButtonDuplicate(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('DUPLICATE')} />;
}

export function KeepAsNew(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('KEEP_AS_NEW')} />;
}

export function BakeasyButtonProceed(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('PROCEED')} />;
}

export function BakeasyButtonConfirm(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CONFIRM')} />;
}

export function BakeasyButtonUpdateShipping(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('UPDATE_SHIPPING_DETAILS')} />;
}

export function BakeasyButtonShippingMemoOnly(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CREATE_SHIPPING_MEMO_ONLY')} />;
}

export function BakeasyButtonShippingMemoInvoice(props: BakeasyButtonProps) {
  return <BakeasyButton {...props} label={t('CREATE_SHIPPING_MEMO_INVOICE')} />;
}
